/**
 * @fileoverview TipTap `diagramBlock` node — an atom block that stores a
 * DiagramSpec (Mermaid source, JSON in a data attribute) and renders it live
 * via a React NodeView. Round-trips through the canvas `html` field so
 * diagrams saved to the canvas persist and re-render on load, and are
 * rasterised to PNG by the PDF/Word export (renderCanvasBlocksForPrint).
 *
 * The source stays editable: the node view has an "Edit source" mode whose
 * edits update the stored spec and re-render in place.
 */
import { useState } from "react";
import { Node, mergeAttributes } from "@tiptap/core";
import {
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from "@tiptap/react";
import { Check, Pencil, X } from "lucide-react";
import type { DiagramSpec } from "@cvx/lib/diagrams/spec";
import { encodeDiagramAttr, diagramSpecFromAttr } from "./diagramHtml";
import { DiagramRenderer } from "../diagramRenderer";
import { DiagramActions } from "../chat/DiagramCard";

function DiagramNodeView({ node, updateAttributes }: ReactNodeViewProps) {
  const spec = node.attrs.spec as DiagramSpec | null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  if (!spec) {
    return (
      <NodeViewWrapper className="my-3">
        <div className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
          Unrenderable diagram
        </div>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper className="my-3">
      <div className="rounded-lg border border-border bg-background p-3">
        <div className="mb-2 flex items-start justify-between gap-2">
          <p className="min-w-0 text-sm font-semibold">{spec.title}</p>
          <div className="flex shrink-0 items-center gap-1">
            {editing ? (
              <>
                <button
                  type="button"
                  title="Apply source changes"
                  onClick={() => {
                    if (draft.trim()) {
                      updateAttributes({ spec: { ...spec, code: draft } });
                    }
                    setEditing(false);
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20"
                >
                  <Check className="h-3 w-3" />
                  Apply
                </button>
                <button
                  type="button"
                  title="Discard source changes"
                  onClick={() => setEditing(false)}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20"
                >
                  <X className="h-3 w-3" />
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  title="Edit Mermaid source"
                  onClick={() => {
                    setDraft(spec.code);
                    setEditing(true);
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs hover:bg-accent/20"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
                <DiagramActions spec={spec} />
              </>
            )}
          </div>
        </div>
        {editing && (
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={Math.min(16, Math.max(4, draft.split("\n").length + 1))}
            className="mb-2 w-full rounded-md border border-border bg-muted/40 p-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          />
        )}
        <DiagramRenderer
          code={editing ? draft : spec.code}
          title={spec.title}
        />
      </div>
    </NodeViewWrapper>
  );
}

export const DiagramNode = Node.create({
  name: "diagramBlock",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      spec: {
        default: null,
        parseHTML: (el: HTMLElement) =>
          diagramSpecFromAttr(el.getAttribute("data-morgan-diagram") ?? ""),
        renderHTML: (attrs: { spec: DiagramSpec | null }) =>
          attrs.spec
            ? { "data-morgan-diagram": encodeDiagramAttr(attrs.spec) }
            : {},
      },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-morgan-diagram]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(DiagramNodeView);
  },
});
